import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '@utils/axios';
import { apiInternalPriceRulePath } from 'routes';
import SearchDropdown from './SearchDropdown';

const priceListInput = document.querySelector('[name*="work_order[price_list_id]"');
const resyncButton = document.querySelector('[data-js-sync-all-prices]');

function MaterialsServicesSelector({
  workOrderCorrection,
  proposedCorrection: initialProposedCorrection,
  priceRule: initialPriceRule,
  correctionName,
  priceRuleName,
  objectName,
  priceListId: initialPriceListId
}) {
  const [proposedCorrection, setProposedCorrection] = useState(initialProposedCorrection || { id: '', price: 0 });
  const [priceListId, setPriceListId] = useState(priceListInput?.value ?? initialPriceListId ?? '');
  const [priceRule, setPriceRule] = useState(initialPriceRule);
  const [price, setPrice] = useState(workOrderCorrection.proposedCorrectionPrice);
  const [firstTimeRender, setFirstTimeRender] = useState(true);
  const input = useRef();
  const [resyncButtonClicked, setResyncButtonClicked] = useState(false);

  useEffect(() => {
    const onPriceListChange = function () {
      setPriceListId(this.value);
    };
    const onResyncClick = () => {
      setResyncButtonClicked(true);
    };

    priceListInput?.addEventListener('change', onPriceListChange);
    resyncButton?.addEventListener('click', onResyncClick);

    // Both handles are module-scoped and shared by every line-item row, so without this
    // each mounted row leaves another listener behind.
    return () => {
      priceListInput?.removeEventListener('change', onPriceListChange);
      resyncButton?.removeEventListener('click', onResyncClick);
    };
  }, []);

  const isWorkOrderCorrectionChanged = useMemo(
    () => workOrderCorrection.proposedCorrectionId?.toString() !== proposedCorrection.id?.toString(),
    [proposedCorrection]
  );

  const isPriceListChanged = useMemo(() => priceListId.toString() !== initialPriceListId?.toString(), [priceListId]);

  useEffect(() => {
    if (firstTimeRender) {
      setFirstTimeRender(false);
      return;
    }

    if (!priceListId || !proposedCorrection?.id) {
      setPriceRule({});
      setPrice(0);
      return;
    }

    if (!isWorkOrderCorrectionChanged && !isPriceListChanged && !resyncButtonClicked) {
      setPriceRule(initialPriceRule);
      setPrice(workOrderCorrection.proposedCorrectionPrice);
      return;
    }

    if (proposedCorrection.description_other) {
      document.querySelector(`[name*="${objectName}[description]"]`).value = proposedCorrection.description_other;
    }

    api
      .get(apiInternalPriceRulePath({ price_list_id: priceListId, proposed_correction_id: proposedCorrection.id }))
      .then(({ data }) => {
        setPriceRule(data.price_rule);
        setPrice(data.price);
        if (resyncButtonClicked) {
          document.querySelector(`[name*="${objectName}[proposed_correction_price]"]`).value = data.price;
          document.querySelector(`[name*="${objectName}[proposed_correction_price]"]`).disabled = false;
        }
      });
  }, [priceListId, proposedCorrection, resyncButtonClicked]);

  const handleSelectionClear = () => {
    document.querySelector(`[name*="${objectName}[description]"]`).value = '';
    setProposedCorrection({ id: '', price: 0 });
  };

  useEffect(() => {
    input.current.dispatchEvent(new Event('change', { bubbles: true }));
  }, [price]);

  return (
    <>
      <input
        type="hidden"
        ref={input}
        name={correctionName}
        value={proposedCorrection?.id}
        data-price={price}
        data-js-work-order-proposed-correction
      />
      <input type="hidden" name={priceRuleName} value={priceRule?.id ?? ''} />
      {proposedCorrection?.id && (
        <div
          className="qmb-input --x-full"
          style={{ display: 'flex', padding: '8px', boxSizing: 'border-box', alignItems: 'center', width: '24rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <small>{proposedCorrection.type}</small>
            <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '.5rem', alignItems: 'baseline' }}>
              {proposedCorrection.description}
              <button type="button" className="qmb-control" style={{ padding: '3px 6px', borderRadius: '10px' }}>
                {/* <small>{priceRule?.asApplicable ?? 'No Price Rule'}</small> */}
              </button>
            </div>
          </div>
          <button type="button" className="qmb-control--icon" onClick={handleSelectionClear}>
            <i className="fa-light fa-xmark" />
          </button>
        </div>
      )}

      <SearchDropdown proposedCorrection={proposedCorrection} setProposedCorrection={setProposedCorrection} />
    </>
  );
}

MaterialsServicesSelector.propTypes = {
  workOrderCorrection: PropTypes.object.isRequired,
  proposedCorrection: PropTypes.object,
  correctionName: PropTypes.string.isRequired,
  priceRuleName: PropTypes.string.isRequired,
  objectName: PropTypes.string.isRequired
};

MaterialsServicesSelector.defaultProps = {
  proposedCorrection: {
    id: '',
    price: 0
  }
};

export default MaterialsServicesSelector;
